import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { UserClaim, ClaimType } from '../../types/user-management';
import { UserManagementService } from '../../services/userManagementService';

interface UserClaimsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  userClaims: UserClaim[];
  availableClaims: ClaimType[];
  onRefresh: () => void;
}

const UserClaimsModal: React.FC<UserClaimsModalProps> = ({
  isOpen,
  onClose,
  userId,
  userName,
  userClaims,
  availableClaims,
  onRefresh
}) => {
  const [selectedClaimType, setSelectedClaimType] = useState('');
  const [claimValue, setClaimValue] = useState('True');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claims, setClaims] = useState<UserClaim[]>(userClaims || []);
  const [initialLoad, setInitialLoad] = useState(true);

  // On open, fetch latest claims live (defensive refresh) if modal just opened
  useEffect(() => {
    const fetchLiveClaims = async () => {
      if (!isOpen) return;
      try {
        setIsLoading(true);
        const live = await UserManagementService.getUserClaims(userId);
        if (live.length > 0 || userClaims.length === 0) {
          setClaims(live);
        } else {
          // Keep passed-in claims if live comes back empty but we had some
          setClaims(userClaims);
        }
      } catch (e) {
        // fallback to provided claims
        setClaims(userClaims);
      } finally {
        setIsLoading(false);
        setInitialLoad(false);
      }
    };
    fetchLiveClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const handleAddClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClaimType) return;

    setIsLoading(true);
    setError(null);

    try {
      await UserManagementService.addClaimToUser(userId, {
        type: selectedClaimType,
        value: claimValue
      });
      // Re-fetch live claims to reflect backend canonical state
      try {
        const updated = await UserManagementService.getUserClaims(userId);
        setClaims(updated);
      } catch {
        setClaims(prev => [...prev, { type: selectedClaimType, value: claimValue }]);
      }
      
      setSelectedClaimType('');
      setClaimValue('True');
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add claim');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveClaim = async (claim: UserClaim) => {
    setIsLoading(true);
    setError(null);

    try {
      await UserManagementService.removeClaimFromUser(userId, claim);
      try {
        const updated = await UserManagementService.getUserClaims(userId);
        setClaims(updated);
      } catch {
        setClaims(prev => prev.filter(c => !(c.type === claim.type && c.value === claim.value)));
      }
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove claim');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            Manage Claims for {userName}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Add new claim */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium mb-4">Add New Claim</h3>
          <form onSubmit={handleAddClaim} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Type
                </label>
                <select
                  value={selectedClaimType}
                  onChange={(e) => setSelectedClaimType(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                >
                  <option value="">Select a claim type</option>
                  {availableClaims.map((claim) => (
                    <option key={claim.name} value={claim.name}>
                      {claim.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value
                </label>
                <input
                  type="text"
                  value={claimValue}
                  onChange={(e) => setClaimValue(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  placeholder="True"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading || !selectedClaimType}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add Claim
            </button>
          </form>
        </div>

        {/* Current claims */}
        <div>
          <h3 className="text-lg font-medium mb-4">Current Claims</h3>
          {isLoading && initialLoad ? (
            <p className="text-gray-500 italic">Loading claims...</p>
          ) : claims.length === 0 ? (
            <p className="text-gray-500 italic">No claims assigned</p>
          ) : (
            <div className="space-y-2">
              {claims.map((claim, index) => (
                <div
                  key={`${claim.type}-${claim.value}-${index}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <span className="font-medium">{claim.type}</span>
                    <span className="text-gray-500 ml-2">= {claim.value}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveClaim(claim)}
                    disabled={isLoading}
                    className="text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserClaimsModal;
